import { useState } from "react";
import {
  BookOpen, Server, Shield, Mail, Terminal, Settings,
  AlertTriangle, ChevronDown, ChevronUp, Copy, Check,
  Zap, Clock, Ban, Users, FileCode, RefreshCw, Eye, Bell
} from "lucide-react";
import { toast } from "sonner";

const API = '/api';

export default function HelpGuide() {
  const [expanded, setExpanded] = useState({ arch: true });
  const [copied, setCopied] = useState(null);

  const toggle = (key) => setExpanded(s => ({ ...s, [key]: !s[key] }));
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const CopyBtn = ({ text, label }) => (
    <button onClick={() => copy(text, label)}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border border-[#E1DFDD] rounded-sm bg-white hover:bg-[#F3F2F1] transition-colors"
      data-testid={`copy-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      {copied === label ? <Check size={10} className="text-[#107C10]" /> : <Copy size={10} />}
      {copied === label ? "Copied" : "Copy"}
    </button>
  );

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="help-guide-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Help & Setup Guide</h1>
        <p className="text-sm text-[#5E5E5E] mt-1">Complete reference for SignatureForge configuration, deployment, and operations.</p>
      </div>

      {/* ── Architecture ── */}
      <Section id="arch" title="Architecture Overview" icon={<Server size={15} />} expanded={expanded.arch} onToggle={() => toggle("arch")}>
        <Pre>{`Sender (Outlook/OWA/Mobile)
    ↓
Exchange Online
    ↓ Outbound Connector (mail flow rule)
    ↓
SMTP Relay (your VPS)
    ↓ Parse MIME → Decide (FULL/SHORT/NONE) → Inject signature → Hold queue
    ↓
Recipient's mail server (via MX lookup)

Internal emails never leave Exchange Online → no signature added.`}</Pre>
        <Info>
          <strong>Control Plane</strong> — This dashboard (SignatureForge). Manages templates, brands, users, rules, and assignments.<br/>
          <strong>Enforcement Plane</strong> — SMTP Relay on your VPS. Intercepts outbound external email from Exchange Online, injects signatures, and forwards to recipients.
        </Info>
      </Section>

      {/* ── Signature Logic ── */}
      <Section id="logic" title="Signature Logic" icon={<Mail size={15} />} expanded={expanded.logic} onToggle={() => toggle("logic")}>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm">
          <thead className="bg-[#FAFAFA]">
            <tr><th className="text-left px-3 py-2 border-b border-[#E1DFDD]">Scenario</th><th className="text-left px-3 py-2 border-b border-[#E1DFDD]">Signature</th><th className="text-left px-3 py-2 border-b border-[#E1DFDD]">Placement</th></tr>
          </thead>
          <tbody>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-2">New external email</td><td className="px-3 py-2 font-medium text-[#0F6CBD]">FULL branded signature</td><td className="px-3 py-2">After user's text</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-2">Reply/Forward to external</td><td className="px-3 py-2 font-medium text-[#D83B01]">SHORT compact signature</td><td className="px-3 py-2">Between reply and quoted thread</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-2">Internal email</td><td className="px-3 py-2 text-[#5E5E5E]">None</td><td className="px-3 py-2">Skipped — never reaches relay</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-2">Email with [nosig]</td><td className="px-3 py-2 text-[#5E5E5E]">None</td><td className="px-3 py-2">Bypassed — forwarded without signature</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-2">System/bypass sender</td><td className="px-3 py-2 text-[#5E5E5E]">None</td><td className="px-3 py-2">Bypassed — forwarded without signature</td></tr>
            <tr><td className="px-3 py-2">User toggled inactive (Staff Directory)</td><td className="px-3 py-2 text-[#5E5E5E]">None</td><td className="px-3 py-2">Bypassed — only users with Active toggle ON get signatures</td></tr>
          </tbody>
        </table>
        <Info>Signatures are placed between the user's reply and the quoted/forwarded thread — not at the end of the email chain. This matches how Exclaimer and similar products work.</Info>
      </Section>

      {/* ── VPS Relay Setup ── */}
      <Section id="vps" title="VPS Relay Setup" icon={<Server size={15} />} expanded={expanded.vps} onToggle={() => toggle("vps")}>
        <H3>Server Requirements</H3>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm mb-4">
          <tbody>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5 font-medium w-32">OS</td><td className="px-3 py-1.5">Ubuntu 22/24 LTS or AlmaLinux 8+</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5 font-medium">CPU / RAM</td><td className="px-3 py-1.5">2+ cores, 4 GB RAM</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5 font-medium">Disk</td><td className="px-3 py-1.5">40 GB SSD</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5 font-medium">Network</td><td className="px-3 py-1.5">Static public IP, port 25 inbound + outbound open</td></tr>
            <tr><td className="px-3 py-1.5 font-medium">Software</td><td className="px-3 py-1.5">Docker + Docker Compose</td></tr>
          </tbody>
        </table>

        <H3>Firewall Rules</H3>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm mb-4">
          <thead className="bg-[#FAFAFA]"><tr><th className="text-left px-3 py-1.5 border-b">Port</th><th className="text-left px-3 py-1.5 border-b">Direction</th><th className="text-left px-3 py-1.5 border-b">Purpose</th></tr></thead>
          <tbody>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5">25</td><td className="px-3 py-1.5">Inbound</td><td className="px-3 py-1.5">Receive mail from Exchange Online</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5">25</td><td className="px-3 py-1.5">Outbound</td><td className="px-3 py-1.5">Deliver mail to recipient MX</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5">8080</td><td className="px-3 py-1.5">Internal</td><td className="px-3 py-1.5">Health check + recall API</td></tr>
            <tr><td className="px-3 py-1.5">22</td><td className="px-3 py-1.5">Inbound</td><td className="px-3 py-1.5">SSH management</td></tr>
          </tbody>
        </table>

        <H3>DNS Records</H3>
        <CodeBlock label="DNS A Record" text={`relay.europacific.com  A  YOUR_VPS_PUBLIC_IP`} copy={copy} copied={copied} />
        <CodeBlock label="SPF Update" text={`v=spf1 include:spf.protection.outlook.com ip4:YOUR_VPS_IP ~all`} copy={copy} copied={copied} />
        <Info>Also set a PTR (reverse DNS) record for the VPS IP pointing to your relay hostname. Contact your hosting provider.</Info>

        <H3>TLS Certificate</H3>
        <CodeBlock label="Certbot" text={`sudo certbot certonly --standalone -d relay.europacific.com
mkdir -p /opt/signatureforge-relay/certs/
sudo cp /etc/letsencrypt/live/relay.europacific.com/fullchain.pem /opt/signatureforge-relay/certs/
sudo cp /etc/letsencrypt/live/relay.europacific.com/privkey.pem /opt/signatureforge-relay/certs/`} copy={copy} copied={copied} />

        <H3>Start Relay</H3>
        <CodeBlock label="Docker Start" text={`cd /opt/signatureforge-relay
docker compose up -d
docker logs -f signatureforge-relay`} copy={copy} copied={copied} />
      </Section>

      {/* ── Exchange Online Setup ── */}
      <Section id="exchange" title="Exchange Online Setup" icon={<Terminal size={15} />} expanded={expanded.exchange} onToggle={() => toggle("exchange")}>
        <H3>Step 1: Connect to Exchange Online PowerShell</H3>
        <CodeBlock label="Connect" text={`Install-Module -Name ExchangeOnlineManagement -Scope CurrentUser
Connect-ExchangeOnline`} copy={copy} copied={copied} />

        <H3>Step 2: Create Outbound Connector</H3>
        <CodeBlock label="Outbound Connector" text={`New-OutboundConnector \`
  -Name "SignatureForge Relay" \`
  -ConnectorType Partner \`
  -RecipientDomains @("*") \`
  -SmartHosts @("relay.europacific.com") \`
  -TlsSettings RequireTls \`
  -UseMXRecord $false \`
  -IsTransportRuleScoped $true \`
  -Comment "Routes external mail through SignatureForge relay for signature injection"`} copy={copy} copied={copied} />

        <H3>Step 3: Create Mail Flow Rule (Pilot)</H3>
        <CodeBlock label="Pilot Rule" text={`New-TransportRule \`
  -Name "SignatureForge Route to Relay - Pilot" \`
  -FromScope InOrganization \`
  -SentToScope NotInOrganization \`
  -From "rok.kobal@europacific.com" \`
  -RouteMessageOutboundConnector "SignatureForge Relay" \`
  -Mode Enforce \`
  -StopRuleProcessing $true \`
  -Comments "Routes pilot user external mail through SignatureForge relay"`} copy={copy} copied={copied} />

        <H3>Step 4: Expand to All Users</H3>
        <CodeBlock label="All Users" text={`Set-TransportRule -Identity "SignatureForge Route to Relay - Pilot" \`
  -Name "SignatureForge Route to Relay" \`
  -From $null`} copy={copy} copied={copied} />

        <H3>Emergency Rollback</H3>
        <CodeBlock label="Rollback" text={`Disable-TransportRule -Identity "SignatureForge Route to Relay" -Confirm:$false
Write-Host "Mail flow rule disabled. Mail routes normally." -ForegroundColor Green`} copy={copy} copied={copied} />
      </Section>

      {/* ── Configuration Reference ── */}
      <Section id="config" title="Relay Configuration (.env)" icon={<Settings size={15} />} expanded={expanded.config} onToggle={() => toggle("config")}>
        <p className="text-xs text-[#5E5E5E] mb-3">All settings are in <code className="bg-[#F3F2F1] px-1 rounded-sm">/opt/signatureforge-relay/.env</code> on the VPS. After changes, restart: <code className="bg-[#F3F2F1] px-1 rounded-sm">docker compose down && docker compose up -d</code></p>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm">
          <thead className="bg-[#FAFAFA]"><tr><th className="text-left px-3 py-2 border-b">Variable</th><th className="text-left px-3 py-2 border-b">Default</th><th className="text-left px-3 py-2 border-b">Description</th></tr></thead>
          <tbody>
            {[
              ["SMTP_PORT", "25", "SMTP listening port"],
              ["SMTP_HOSTNAME", "relay.europacific.com", "SMTP banner hostname"],
              ["SMTP_TLS_CERT", "", "Path to TLS certificate file"],
              ["SMTP_TLS_KEY", "", "Path to TLS private key"],
              ["ALLOWED_SOURCES", "Exchange Online IPs", "Comma-separated IPs/CIDRs allowed to connect"],
              ["MONGO_URL", "mongodb://127.0.0.1:27017", "MongoDB connection string"],
              ["DB_NAME", "signatureforge", "Database name"],
              ["DRY_RUN", "true", "Process but don't inject signatures. Set false for production."],
              ["PILOT_MODE", "true", "Only process emails from PILOT_ALLOWLIST senders"],
              ["PILOT_ALLOWLIST", "rok.kobal@europacific.com", "Comma-separated pilot email addresses"],
              ["NOSIG_KEYWORD", "[nosig]", "Keyword in subject/body to skip signature injection"],
              ["BYPASS_SENDERS", "noreply@myeuropacific.com", "Comma-separated sender addresses to always skip"],
              ["HOLD_ENABLED", "true", "Enable email hold queue for recall support"],
              ["HOLD_SECONDS", "120", "Seconds to hold emails before auto-release (default 2 min)"],
              ["HEALTH_PORT", "8080", "HTTP port for health check and recall API"],
              ["PUSHOVER_USER_KEY", "", "Pushover user key for health alerts (from pushover.net)"],
              ["PUSHOVER_API_TOKEN", "", "Pushover app token for health alerts"],
            ].map(([k, v, d]) => (
              <tr key={k} className="border-b border-[#F3F2F1]">
                <td className="px-3 py-1.5 font-mono font-medium">{k}</td>
                <td className="px-3 py-1.5 text-[#5E5E5E]">{v}</td>
                <td className="px-3 py-1.5">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* ── User Activation ── */}
      <Section id="activation" title="User Activation — Staff Directory" icon={<Users size={15} />} expanded={expanded.activation} onToggle={() => toggle("activation")}>
        <p className="text-xs text-[#5E5E5E] mb-3">Only users with the <strong>Active</strong> toggle enabled in the Staff Directory get signatures. All other users' emails pass through the relay without modification.</p>
        <H3>How It Works</H3>
        <ol className="text-xs text-[#5E5E5E] space-y-1.5 list-decimal list-inside mb-4">
          <li>Go to <strong>Staff Directory</strong> in the sidebar</li>
          <li>Find the user and click the <strong>toggle switch</strong> in the Active column</li>
          <li>Green = signatures enabled, Grey = signatures disabled</li>
          <li>Active users are sorted to the top of the list</li>
          <li>Use the <strong>All / Active / Inactive</strong> filter buttons to quickly find users</li>
        </ol>
        <H3>Relay Behavior</H3>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm mb-3">
          <tbody>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5 font-medium w-40">Active user sends email</td><td className="px-3 py-1.5">Signature is injected (FULL or SHORT based on logic)</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5 font-medium">Inactive user sends email</td><td className="px-3 py-1.5">Email passes through without any signature</td></tr>
            <tr><td className="px-3 py-1.5 font-medium">Unknown sender (not in directory)</td><td className="px-3 py-1.5">Email passes through without signature</td></tr>
          </tbody>
        </table>
        <Info>This replaces the need to manually manage bypass lists for individual users. Just toggle them off in the Staff Directory.</Info>
      </Section>

      {/* ── [nosig] Bypass ── */}
      <Section id="nosig" title="[nosig] Bypass — Skip Signature" icon={<Ban size={15} />} expanded={expanded.nosig} onToggle={() => toggle("nosig")}>
        <p className="text-xs text-[#5E5E5E] mb-3">Include <code className="bg-[#F3F2F1] px-1 rounded-sm font-mono">[nosig]</code> anywhere in the email <strong>subject</strong> or <strong>body</strong> to skip signature injection for that email.</p>
        <H3>Examples for Your Systems</H3>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm mb-4">
          <thead className="bg-[#FAFAFA]"><tr><th className="text-left px-3 py-2 border-b">Method</th><th className="text-left px-3 py-2 border-b">Example</th></tr></thead>
          <tbody>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5">In subject</td><td className="px-3 py-1.5 font-mono">Invoice #12345 [nosig]</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5">Hidden in HTML body</td><td className="px-3 py-1.5 font-mono">&lt;span style="display:none"&gt;[nosig]&lt;/span&gt;</td></tr>
            <tr><td className="px-3 py-1.5">In plain text body</td><td className="px-3 py-1.5 font-mono">This is an automated alert. [nosig]</td></tr>
          </tbody>
        </table>
        <H3>Change the Keyword</H3>
        <p className="text-xs text-[#5E5E5E] mb-2">Edit on the VPS:</p>
        <CodeBlock label="Change Keyword" text={`# Edit .env on VPS
ssh root@YOUR_VPS -p 19193
nano /opt/signatureforge-relay/.env
# Change: NOSIG_KEYWORD=[your-custom-keyword]
# Restart:
cd /opt/signatureforge-relay && docker compose down && docker compose up -d`} copy={copy} copied={copied} />
      </Section>

      {/* ── Bypass Senders ── */}
      <Section id="bypass" title="Bypass Senders — System Emails" icon={<Shield size={15} />} expanded={expanded.bypass} onToggle={() => toggle("bypass")}>
        <p className="text-xs text-[#5E5E5E] mb-3">Emails FROM these addresses are always forwarded without signature injection. Use for system accounts, automated notifications, and shared mailboxes that already have embedded signatures.</p>
        <H3>Current Bypass List</H3>
        <Pre>{`BYPASS_SENDERS=noreply@myeuropacific.com,system@myeuropacific.com`}</Pre>
        <H3>Add More Senders</H3>
        <p className="text-xs text-[#5E5E5E] mb-2">You can add individual addresses or entire domains:</p>
        <CodeBlock label="Bypass Examples" text={`# Individual addresses:
BYPASS_SENDERS=noreply@myeuropacific.com,system@myeuropacific.com,invoices@europacific.com

# Entire domain (all @myeuropacific.com emails bypass):
BYPASS_SENDERS=myeuropacific.com,noreply@europacific.com`} copy={copy} copied={copied} />
      </Section>

      {/* ── Hold & Recall ── */}
      <Section id="recall" title="Email Hold & Recall" icon={<Clock size={15} />} expanded={expanded.recall} onToggle={() => toggle("recall")}>
        <p className="text-xs text-[#5E5E5E] mb-3">All processed outbound emails are held for a configurable window before being delivered. During this window, emails can be recalled (cancelled).</p>
        <H3>How It Works</H3>
        <ol className="text-xs text-[#5E5E5E] space-y-1.5 list-decimal list-inside mb-4">
          <li>Email arrives at relay from Exchange Online</li>
          <li>Relay processes signature injection</li>
          <li>Email is stored in hold queue (default: 2 minutes)</li>
          <li>If recalled within the window — email is cancelled, never delivered</li>
          <li>If not recalled — email is automatically forwarded to recipient</li>
        </ol>
        <H3>Recall via API</H3>
        <CodeBlock label="List Held Emails" text={`curl http://YOUR_VPS:8080/held`} copy={copy} copied={copied} />
        <CodeBlock label="Recall an Email" text={`curl -X POST http://YOUR_VPS:8080/recall \\
  -H "Content-Type: application/json" \\
  -d '{"hold_id": "EMAIL_HOLD_ID", "reason": "user_recall"}'`} copy={copy} copied={copied} />
        <CodeBlock label="Change Hold Duration" text={`# Runtime change (no restart needed):
curl -X POST http://YOUR_VPS:8080/config \\
  -H "Content-Type: application/json" \\
  -d '{"hold_seconds": 300}'

# Permanent change: edit .env
HOLD_SECONDS=300`} copy={copy} copied={copied} />
        <H3>O365 Recall Integration</H3>
        <p className="text-xs text-[#5E5E5E]">If a user triggers "Recall Message" in Outlook, Exchange sends a recall request. The relay detects this and automatically cancels the matching held email (if still in the hold window).</p>
      </Section>

      {/* ── Pilot to Production ── */}
      <Section id="golive" title="Pilot → Production Workflow" icon={<Zap size={15} />} expanded={expanded.golive} onToggle={() => toggle("golive")}>
        <ol className="text-xs text-[#5E5E5E] space-y-3 list-none mb-4">
          {[
            ["1. DRY_RUN + PILOT", "Start with DRY_RUN=true and PILOT_MODE=true. Emails are processed but signatures are not injected. Verify in relay logs."],
            ["2. Enable injection for pilot", "Set DRY_RUN=false. Keep PILOT_MODE=true. Only pilot users get signatures."],
            ["3. Test all scenarios", "Send: new external (→ full sig), reply external (→ short sig), internal (→ no sig), [nosig] email (→ skip)."],
            ["4. Verify placement", "Check that signatures appear between user's reply and quoted thread, not at the end."],
            ["5. Expand pilot", "Add more users to PILOT_ALLOWLIST. Test with each."],
            ["6. Full deployment", "Set PILOT_MODE=false. Update Exchange mail flow rule to remove -From filter."],
            ["7. Monitor 24h", "Watch relay logs and health endpoint. Keep rollback scripts ready."],
          ].map(([step, desc]) => (
            <li key={step}><span className="font-medium text-[#171717]">{step}</span><br/>{desc}</li>
          ))}
        </ol>
        <Warn>Always keep the Exchange rollback script ready. One command disables the mail flow rule and returns to normal routing.</Warn>
      </Section>

      {/* ── Relay API ── */}
      <Section id="api" title="Relay API Reference" icon={<FileCode size={15} />} expanded={expanded.api} onToggle={() => toggle("api")}>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm">
          <thead className="bg-[#FAFAFA]"><tr><th className="text-left px-3 py-2 border-b">Endpoint</th><th className="text-left px-3 py-2 border-b">Method</th><th className="text-left px-3 py-2 border-b">Description</th></tr></thead>
          <tbody>
            {[
              ["/health", "GET", "Relay status, held email count"],
              ["/stats", "GET", "Processing statistics, recent emails"],
              ["/held", "GET", "List currently held emails (?sender=email to filter)"],
              ["/recent", "GET", "Recently released/recalled emails (?limit=20)"],
              ["/recall", "POST", "Cancel a held email: {hold_id, reason}"],
              ["/config", "POST", "Update hold config: {hold_seconds, hold_enabled}"],
            ].map(([ep, method, desc]) => (
              <tr key={ep} className="border-b border-[#F3F2F1]">
                <td className="px-3 py-1.5 font-mono">{ep}</td>
                <td className="px-3 py-1.5">{method}</td>
                <td className="px-3 py-1.5">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* ── Pushover Alerts ── */}
      <Section id="pushover" title="Pushover Health Alerts" icon={<Bell size={15} />} expanded={expanded.pushover} onToggle={() => toggle("pushover")}>
        <p className="text-xs text-[#5E5E5E] mb-3">The relay sends real-time push notifications to your phone via Pushover when health issues are detected.</p>
        <H3>Alerts You Receive</H3>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm mb-4">
          <thead className="bg-[#FAFAFA]"><tr><th className="text-left px-3 py-2 border-b">Event</th><th className="text-left px-3 py-2 border-b">Priority</th><th className="text-left px-3 py-2 border-b">When</th></tr></thead>
          <tbody>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5 font-medium text-[#A4262C]">Relay Unhealthy</td><td className="px-3 py-1.5">High</td><td className="px-3 py-1.5">MongoDB down, emails failing to deliver, or hold queue stuck</td></tr>
            <tr className="border-b border-[#F3F2F1]"><td className="px-3 py-1.5 font-medium text-[#107C10]">Relay Recovered</td><td className="px-3 py-1.5">Low</td><td className="px-3 py-1.5">All systems back to normal after an unhealthy state</td></tr>
            <tr><td className="px-3 py-1.5 font-medium text-[#0F6CBD]">Relay Started</td><td className="px-3 py-1.5">Low</td><td className="px-3 py-1.5">Relay container starts or restarts</td></tr>
          </tbody>
        </table>
        <H3>Configuration</H3>
        <p className="text-xs text-[#5E5E5E] mb-2">Pushover credentials are in the relay <code className="bg-[#F3F2F1] px-1 rounded-sm">.env</code> file:</p>
        <CodeBlock label="Pushover Config" text={`PUSHOVER_USER_KEY=your_user_key
PUSHOVER_API_TOKEN=your_app_token`} copy={copy} copied={copied} />
        <H3>How to Get Credentials</H3>
        <ol className="text-xs text-[#5E5E5E] space-y-1 list-decimal list-inside mb-3">
          <li>Sign up at <a href="https://pushover.net" target="_blank" rel="noopener noreferrer" className="text-[#0F6CBD] underline">pushover.net</a></li>
          <li>Install the Pushover app on your phone (iOS/Android)</li>
          <li>Your <strong>User Key</strong> is on the dashboard home page</li>
          <li>Create an application at <a href="https://pushover.net/apps/build" target="_blank" rel="noopener noreferrer" className="text-[#0F6CBD] underline">pushover.net/apps/build</a> to get the <strong>API Token</strong></li>
        </ol>
        <H3>Monitoring Interval</H3>
        <p className="text-xs text-[#5E5E5E]">Health is checked every <strong>60 seconds</strong>. Alerts fire only on state <em>transitions</em> (healthy → unhealthy, unhealthy → healthy) — no repeated alerts for the same issue.</p>
      </Section>

      {/* ── Troubleshooting ── */}
      <Section id="trouble" title="Troubleshooting" icon={<AlertTriangle size={15} />} expanded={expanded.trouble} onToggle={() => toggle("trouble")}>
        <table className="w-full text-xs border border-[#E1DFDD] rounded-sm">
          <thead className="bg-[#FAFAFA]"><tr><th className="text-left px-3 py-2 border-b">Problem</th><th className="text-left px-3 py-2 border-b">Check</th></tr></thead>
          <tbody>
            {[
              ["No mail arriving at relay", "Verify Exchange outbound connector + mail flow rule are active. Check: Get-TransportRule | Where {$_.Name -like 'Signature*'}"],
              ["Connection refused port 25", "Check firewall. AlmaLinux: firewall-cmd --add-port=25/tcp. Ubuntu: ufw allow 25/tcp"],
              ["TLS handshake failure", "Verify cert paths in .env. Check cert expiry: openssl x509 -noout -dates -in /opt/signatureforge-relay/certs/fullchain.pem"],
              ["Signatures not appearing", "Check DRY_RUN=false in .env. Check relay logs: docker logs signatureforge-relay | tail -30"],
              ["Mail not delivered after relay", "Check outbound port 25 is open. Many hosting providers block it by default."],
              ["'not_in_pilot' in logs", "Add sender to PILOT_ALLOWLIST in .env and restart"],
              ["'no_template_assigned'", "Assign a default template to the sender's brand in the dashboard"],
              ["Hold queue not releasing", "Check HOLD_ENABLED=true. Monitor: curl http://localhost:8080/held"],
              ["Relay container not starting", "Check logs: docker logs signatureforge-relay. Common: port 25 already in use (stop exim/postfix)"],
            ].map(([prob, check]) => (
              <tr key={prob} className="border-b border-[#F3F2F1]">
                <td className="px-3 py-2 font-medium">{prob}</td>
                <td className="px-3 py-2">{check}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <div className="h-8" />
    </div>
  );
}

/* ── Reusable components ── */

function Section({ id, title, icon, expanded, onToggle, children }) {
  return (
    <div className="mb-3 bg-white border border-[#E1DFDD] rounded-sm" data-testid={`section-${id}`}>
      <button onClick={onToggle} className="w-full px-5 py-3 flex items-center justify-between hover:bg-[#FAFAFA] transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-[#0F6CBD]">{icon}</span>
          <h2 className="font-heading text-sm font-medium text-[#171717]">{title}</h2>
        </div>
        {expanded ? <ChevronUp size={14} className="text-[#5E5E5E]" /> : <ChevronDown size={14} className="text-[#5E5E5E]" />}
      </button>
      {expanded && <div className="px-5 pb-4 border-t border-[#E1DFDD] pt-3">{children}</div>}
    </div>
  );
}

function H3({ children }) { return <h3 className="text-xs font-semibold text-[#171717] mt-4 mb-2">{children}</h3>; }
function Pre({ children }) { return <pre className="bg-[#1E1E1E] text-[#D4D4D4] text-[11px] font-mono p-3 rounded-sm overflow-x-auto mb-3 whitespace-pre-wrap">{children}</pre>; }
function Info({ children }) { return <div className="bg-[#EBF3FC] border border-[#0F6CBD]/20 rounded-sm p-3 text-xs text-[#5E5E5E] mt-3">{children}</div>; }
function Warn({ children }) { return <div className="bg-[#FFF4CE] border border-[#D83B01]/20 rounded-sm p-3 text-xs text-[#5E5E5E] mt-3 flex items-start gap-2"><AlertTriangle size={13} className="text-[#D83B01] shrink-0 mt-0.5" /><span>{children}</span></div>; }

function CodeBlock({ label, text, copy, copied }) {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-[#5E5E5E]">{label}</span>
        <button onClick={() => copy(text, label)}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border border-[#E1DFDD] rounded-sm bg-white hover:bg-[#F3F2F1]">
          {copied === label ? <Check size={10} className="text-[#107C10]" /> : <Copy size={10} />}
          {copied === label ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="bg-[#1E1E1E] text-[#D4D4D4] text-[11px] font-mono p-3 rounded-sm overflow-x-auto whitespace-pre-wrap">{text}</pre>
    </div>
  );
}
